// DomainRepeat + radialDistToWarpedAxis primitives — plan §4.1 (wood planks).
//
// `domainRepeatAxis` tiles the plane into board cells (periodic across one axis, broken into
// board lengths along the other, with a per-row stagger) and hands back board-local coords, a
// stable per-board cellID for reseeding, and the distance to the nearest board edge (the gap).
// `radialDistToWarpedAxis` is the single-use primitive the plan flags for wood: distance from a
// noise-warped centre line, which sinusoidally banded gives growth rings.
//
// Both are prelude helpers (implemented, NOT layer-usable): the layer-usable wood pattern is the
// `domainRepeat` registry entry's layerBody, which composes both plus waves + valueNoise grain.
//
// NOTE (repo hazard): no backticks, no GLSL-ES-300 reserved identifiers inside the literal.
export const DOMAIN_REPEAT_GLSL = `
struct RepeatSample { vec2 pLocal; int cellID; float edge; };

// Board lattice.  period = board WIDTH (meters, across-grain); lengthPeriod = board LENGTH
// (meters, along-grain); stagger 0..1 offsets each row along its length by a per-row hash
// (0 = aligned butt joints, 1 = fully random offsets).
// pLocal is board-local meters: x along the board from its start, y across it (0 at centre).
// edge = meters to the nearest board boundary (the gap/bevel driver).
RepeatSample domainRepeatAxis(vec2 p, float period, float lengthPeriod, float stagger) {
  float per = max(period, 1e-4);
  float lp = max(lengthPeriod, per);
  float row = floor(p.y / per);
  float xOff = hash01(uvec2(uint(int(row) * 2654435761), 7u)) * lp * clamp(stagger, 0.0, 1.0);
  float col = floor((p.x + xOff) / lp);
  uvec2 u = uvec2(ivec2(int(col), int(row)));
  RepeatSample s;
  s.cellID = int(hash01(u) * 1000000.0);
  float fy = p.y / per - row;
  float fx = (p.x + xOff) / lp - col;
  s.pLocal = vec2(fx * lp, (fy - 0.5) * per);
  s.edge = min(min(fx, 1.0 - fx) * lp, min(fy, 1.0 - fy) * per);
  return s;
}

// Distance from a noise-warped axis line through the origin (plan §4.1, wood-only).  axisDir
// need not be normalised.  warpScale = the wavelength of the warp along the axis (meters);
// warpAmount = its magnitude across the axis (meters).
float radialDistToWarpedAxis(vec2 p, vec2 axisDir, float warpScale, float warpAmount) {
  vec2 d = normalize(axisDir);
  vec2 nrm = vec2(-d.y, d.x);
  float along = dot(p, d);
  float across = dot(p, nrm);
  float warp = (valueNoise(vec2(along / max(warpScale, 1e-4), 0.37)) - 0.5) * 2.0 * warpAmount;
  return abs(across + warp);
}
`;
